var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Component, Vue } from 'vue-property-decorator';
import { Guardian as GuardianModel } from '@/models/Guardian';
import PageHeader from '@/components/page-header/PageHeader';
import { fileToBase64, avatarHtml, formatDate } from '@/support/String';
import DataTable from '@/components/data-table/DataTable';
import { Student } from '@/models/Student';
import { Token } from '@/support/Token';
import ImageInput from '@/components/dialog/image-input/ImageInput';
import ErrorHandler from '@/support/ErrorHandler';
import { cloneDeep } from 'lodash';
import { RolesEnum } from '@/items/Roles';
import ConfirmRemoveMfaDialog from '@/components/ConfirmRemoveMfaDialog/ConfirmRemoveMfaDialog.vue';
let Guardian = class Guardian extends Vue {
    constructor() {
        super(...arguments);
        this.$pageTitle = 'Guardian';
        this.isEditing = false;
        this.isEditingDob = false;
        this.formatDob = formatDate;
        this.guardian = null;
        this.guardianClone = new GuardianModel();
        this.file = null;
        this.baseImage = null;
        this.isChangingAvatar = false;
        this.passwordHidden = true;
        this.password = {
            old: '',
            new: '',
            confirmation: '',
        };
        // #region RemoveMFA
        this.isConfirmRemoveMfaDialogOpen = false;
        // #endregion
    }
    get breadcrumbs() {
        return [
            { name: this.$tc('roles.user', 2) },
            { name: this.$tc('roles.guardian', 2), path: '/guardians' },
            { name: this.guardian.name || '', path: this.$route.path },
        ];
    }
    get canEditProfileFields() {
        const setting = this.$store.state.settings.find((setting) => {
            return setting.key === 'parnassys_status';
        });
        const status = setting && setting.value;
        switch (status) {
            case '1':
            case '2':
                return !(this.guardianClone && this.guardianClone.parnassys_id);
            default:
                return true;
        }
    }
    get tableOptions() {
        return {
            name: {
                singular: 'Guardian',
                plural: 'Guardians',
            },
            headers: [
                {
                    text: '',
                    value: 'avatar',
                    transform: avatarHtml,
                },
                {
                    text: this.$t('name.title'),
                    value: 'name',
                    action: '/students/{id}',
                },
            ],
            actions: [
                {
                    type: 'view',
                    label: 'view',
                    icon: 'remove_red_eye',
                    action: '/students/{id}',
                },
            ],
            filters: [],
            dropdown: [],
            model: new Student().filter('relationship', {
                type: 'child',
                user: this.guardian ? this.guardian.id : '',
            }),
        };
    }
    mounted() {
        new GuardianModel()
            .include('token')
            .find(this.$route.params.id)
            .then((guardian) => {
            this.guardian = guardian;
            this.guardianClone = cloneDeep(guardian);
        });
    }
    update() {
        if (!this.guardian) {
            return;
        }
        this.isEditing = false;
        if (this.password.new.length === 0) {
            this.resetPassword();
        }
        else {
            this.guardian.password = this.password.new;
        }
        const payload = this.guardian.getPayload();
        if (payload.avatar) {
            delete payload.avatar;
        }
        this.guardian
            .include(['guardian', 'roles'])
            .update(payload)
            .then((guardian) => {
            this.resetPassword();
            this.guardian = guardian;
            this.guardianClone = cloneDeep(guardian);
            this.updateCurrentUser(guardian);
        });
    }
    resetPassword() {
        if (!this.guardian) {
            return;
        }
        delete this.guardian.password;
        delete this.guardian.current_password;
        this.password = {
            old: '',
            new: '',
            confirmation: '',
        };
    }
    updateCurrentUser(guardian) {
        if (guardian.id === this.$store.state.Auth.id) {
            this.$store.dispatch('userAuthenticated', guardian);
        }
    }
    setToken() {
        Token.setLoginAsToken(this.guardian.token.bearer);
        const url = `${window.location.protocol}//${window.location.host}`;
        window.location.href = url;
    }
    sync(guardian) {
        this.guardian = guardian;
    }
    refreshDataTable() {
        document.dispatchEvent(new CustomEvent('datatable:hook', {
            detail: (dataTable) => {
                dataTable.refresh();
            },
        }));
    }
    changeAvatar(event) {
        if (event.target === null || event.target.files === null || !event.target.files.length) {
            return;
        }
        this.file = event.target.files[0];
        fileToBase64(this.file).then((encodedFile) => {
            this.baseImage = encodedFile;
            this.isChangingAvatar = true;
        });
    }
    changingCurrentAvatar() {
        if (!this.guardian || !this.guardian.avatar) {
            return;
        }
        const canvas = document.createElement('canvas');
        const img = new Image();
        const context = canvas.getContext('2d');
        img.crossOrigin = 'Anonymous';
        img.src = this.guardian.avatar;
        if (!img) {
            return;
        }
        if (!context) {
            return;
        }
        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            context.drawImage(img, 0, 0, img.width, img.height);
            this.baseImage = canvas.toDataURL();
            this.isChangingAvatar = true;
        };
    }
    avatarChanged(imageUrl) {
        if (!this.guardian) {
            return;
        }
        this.guardian
            .include(['guardian', 'roles'])
            .update({ avatar: imageUrl })
            .then((guardian) => {
            this.guardian.avatar = imageUrl;
            this.updateCurrentUser(guardian);
            this.guardianClone = cloneDeep(guardian);
            this.$emit('closed', false);
            this.isChangingAvatar = false;
        })
            .catch((error) => {
            ErrorHandler.network(error);
        });
    }
    get isAuthorized() {
        if (!this.guardian) {
            return false;
        }
        return ((this.$store.state.Auth.hasRole([RolesEnum.GUARDIAN]) &&
            this.$store.state.Auth.id === this.guardian.id) ||
            this.$store.state.Auth.hasRole([
                RolesEnum.ADMIN,
                RolesEnum.ROOT,
                RolesEnum.TRAINER,
                RolesEnum.MANAGER,
            ]));
    }
    get canRemoveMfa() {
        return this.$store.state.Auth.hasRole([RolesEnum.ROOT, RolesEnum.ADMIN]);
    }
    get removeMfaEnabled() {
        return !!this.guardian && !!this.guardian.has_mfa_token;
    }
    async removeMfa() {
        if (this.guardian) {
            const guardian = await this.guardian
                .update({ mfa_token: null })
                .catch((error) => {
                ErrorHandler.network(error);
                return null;
            });
            if (guardian) {
                this.$set(this.guardian, 'has_mfa_token', false);
                if (this.guardianClone) {
                    this.$set(this.guardianClone, 'has_mfa_token', false);
                }
            }
        }
        this.isConfirmRemoveMfaDialogOpen = false;
    }
};
Guardian = __decorate([
    Component({
        components: {
            DataTable,
            PageHeader,
            ImageInput,
            ConfirmRemoveMfaDialog,
        },
    })
], Guardian);
export default Guardian;
